import { CurecallConfirmationModal, ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { PlanningService } from 'src/services/planning.service'
import { useQueryClient } from '@tanstack/react-query'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { getErrorMessage } from 'src/utils/http.util'
import { TPlanningTab } from '../planning.type'
import { ALL_PLANNING_TAB_ID } from '../planning-all-tab.util'
import { useNavigate } from 'react-router-dom'

interface PlanningTabDeleteModalProps {
  tabToDelete: TPlanningTab | null
  onClose: () => void
}

export default function PlanningTabDeleteModal({ tabToDelete, onClose }: PlanningTabDeleteModalProps) {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const navigate = useNavigate()

  const onDelete = async () => {
    if (!tabToDelete?.id) return

    try {
      await PlanningService.deletePlanningTab(project.id, tabToDelete.id)

      await queryClient.invalidateQueries({ queryKey: ['project', project.id] })

      notify(
        {
          title: t('pages.planning.delete-tab.success-title'),
          message: t('pages.planning.delete-tab.success-message', { tabName: tabToDelete.label }),
          appearance: ECurecallNotificationAppearance.SUCCESS,
        },
        15000,
      )

      navigate(`?tabId=${ALL_PLANNING_TAB_ID}`, { replace: true })
    } catch (error) {
      notify(
        {
          title: t('pages.planning.delete-tab.error-title'),
          message: getErrorMessage(error),
          appearance: ECurecallNotificationAppearance.ERROR,
        },
        15000,
      )
    } finally {
      onClose()
    }
  }

  return (
    <CurecallConfirmationModal
      title={t('pages.planning.delete-tab.title')}
      isOpen={true}
      onCancel={onClose}
      onConfirm={onDelete}
      cancelButtonLabel={t('pages.planning.delete-tab.cancel')}
      confirmButtonLabel={t('pages.planning.delete-tab.confirm')}
      color="ALERT"
    >
      <div className="c-text-lg c-font-bold">
        {tabToDelete && <p>{t('pages.planning.delete-tab.description', { tabName: tabToDelete.label })}</p>}
      </div>
    </CurecallConfirmationModal>
  )
}
